import { useAuth } from "app/context/auth.context";
import { useFeaturesContext } from "app/context/features.context";
import { useGoBackEvent } from "app/hooks/go-back.hook";
import { useTranslate } from "app/hooks/lang";
import { useNavigatePath } from "app/hooks/navigation.hook";
import { selectUserState } from "app/store/slices/user";
import { useAppSelector } from "app/store/store";
import clsx from "clsx";
import { useCallback, useEffect, useMemo, useState } from "react";
import QRCode from "react-qr-code";
import { useInterval } from "react-use";
import { usePostPillowBillingStripeV2Payment_link } from "services-hooks/hooks";
import { postPillowBillingStripeV2Payment_link } from "services-hooks/services";
import { BackButton } from "../back-button/back-button.component";
import { Session } from "services-hooks/types";
import MemoAddToPlaylist from "../icons/AddToPlaylist";
import MemoHighQualityOutlined from "../icons/HighQualityOutlined";
import MemoLoader from "../icons/Loader";
import MemoNoAds from "../icons/NoAds";
import MemoWindbackBig from "../icons/WindbackBig";
import MemoWorldsBest from "../icons/WorldsBest";
import { AuthGuardPopup } from "./auth.component";
import styles from "./premium-auth-guard.module.scss";
import { usePlayerContext } from "app/context/player.context";

interface PremiumAuthGuardProps {
  onClose: VoidFunction;
  onAuth: VoidFunction;
  className?: string;
}

export const PremiumAuthGuard: React.FC<PremiumAuthGuardProps> = ({
  onClose,
  onAuth,
  className,
}) => {
  const { premiumVersion } = useFeaturesContext();
  const { session } = useAppSelector(selectUserState);
  const { showQRCode } = useFeaturesContext();
  const isLoggedAndShowQR = showQRCode && session?.user;
  const t = useTranslate();
  const goBackCallback = useCallback(() => {
    if (onClose) {
      onClose();
    }
  }, [onClose]);
  useGoBackEvent("PremiumAuthGuard", goBackCallback);

  const { priceData } = usePlayerContext();

  const {
    data: stripeLinkData,
    mutate: loadStripeLink,
    isLoading: isStripeLoading,
  } = usePostPillowBillingStripeV2Payment_link();

  const QRValue = useMemo(() => {
    return stripeLinkData?.data.data.url;
  }, [stripeLinkData?.data.data.url]);

  const navigatePath = useNavigatePath();

  useEffect(() => {
    if (session?.user) {
      if (showQRCode) {
        loadStripeLink({
          requestBody: {},
        });
      } else {
        loadStripeLink({
          requestBody: {
            redirect_url: `${window.location.origin}/get-premium`,
          },
        });
      }
    }
  }, [session?.user?.id]);

  useEffect(() => {
    if (premiumVersion || session?.user?.premium) {
      navigatePath("/");
    }
  }, [premiumVersion, session?.user?.premium]);

  const { fetchUserData, updateUserData } = useAuth();
  useInterval(async () => {
    if (session?.user && !session?.user?.premium) {
      const resp = await fetchUserData();
      if (resp.data.data.premium) {
        updateUserData(resp);
      }
    }
  }, 1000);

  useEffect(() => {
    if (session?.user?.premium) {
      navigatePath("/settings");
    }
  }, [session?.user?.premium]);

  const goToBuyPremium = async () => {
    if (session?.user && stripeLinkData?.data.data.url) {
      try {
        window.location.href = stripeLinkData.data.data.url;
      } catch (e) {
        console.error(e);
      }
    } else if (!session?.user) {
      setShowAuth(true);
    }
  };

  const [showAuth, setShowAuth] = useState(false);

  const onAuthSuccess = async (session: Session) => {
    setShowAuth(false);
    if (!session.user?.premium && !showQRCode) {
      try {
        const stripeUrl = await postPillowBillingStripeV2Payment_link({
          redirect_url: `${window.location.origin}/get-premium`,
        });
        window.location.href = stripeUrl.data.data.url;
      } catch (e) {}
    } else {
      onAuth();
    }
  };
  const authCloseCallback = useCallback(() => {
    setShowAuth(false);
  }, [setShowAuth]);
  const authSuccessCallback = useCallback(
    (session: Session) => onAuthSuccess(session),
    [onAuthSuccess]
  );
  return (
    <div className={clsx(styles.wrapper, className)}>
      <div className={styles.back_wrapper} data-navigate-row>
        <BackButton onClick={onClose} />
      </div>
      <div
        className={clsx(styles.inner_wrapper, {
          [styles.show_qr]: isLoggedAndShowQR,
        })}
      >
        <div
          className={clsx(styles.go_premium, {
            [styles.show_qr]: isLoggedAndShowQR,
          })}
          data-navigate-row
        >
          {isLoggedAndShowQR && (
            <div className={styles.go_premium_text}>
              {isStripeLoading ? (
                <MemoLoader />
              ) : (
                <>
                  <span>
                    <>
                      {t("premium.go-premium-for", {
                        var1: `${priceData.amount}${priceData.currency_symbol}`,
                      })}
                    </>
                  </span>
                  <span>{t("premium.scan-qr-code")}</span>
                </>
              )}
            </div>
          )}

          {!isLoggedAndShowQR && (
            <button
              onClick={goToBuyPremium}
              data-navigate-item
              className={styles.go_premium_button}
            >
              <span>
                {isStripeLoading ? (
                  <MemoLoader dark />
                ) : (
                  <>{t("premium.go-premium-for", { var1: `${priceData.amount}${priceData.currency_symbol}` })}</>
                )}
              </span>
            </button>
          )}
          {isLoggedAndShowQR && (
            <div className={styles.qr_wrapper}>
              <QRCode value={QRValue || ""} size={231} />
            </div>
          )}
        </div>

        <div className={styles.benefits_wrapper}>
          <div className={styles.benefits1}>
            <div className={clsx(styles.box, styles.box_noads)}>
              <div className={styles.icon}>
                <MemoNoAds />
              </div>
              <div className={styles.text}>
                {t("premium.no-ads")}
                <p>{t("premium.no-ads-desc")}</p>
              </div>
            </div>

            <div className={clsx(styles.box, styles.box_playlist)}>
              <div className={styles.icon}>
                <MemoAddToPlaylist color="#000" />
              </div>
              <div className={styles.text}>
                {t("export-to-playlist")}
                <p>{t("premium.export-to-playlist-desc")}</p>
              </div>
            </div>

            <div className={clsx(styles.box, styles.box_autoplay)}>
              <div className={styles.icon}>
                <MemoWindbackBig />
              </div>
              <div className={styles.text}>
                {t("autoplay")}
                <p>{t("premium.automatically-play-last-radio")}</p>
              </div>
            </div>
          </div>

          <div className={styles.benefits2}>
            <div className={clsx(styles.box, styles.box_worlds_best)}>
              <div className={styles.icon}>
                <MemoWorldsBest />
              </div>
              <div className={styles.text}>
                {t("premium.worlds-best")}
                <p>{t("premium.worlds-best-desc")}</p>
              </div>
            </div>

            <div className={clsx(styles.box, styles.box_hq)}>
              <div className={styles.icon}>
                <MemoHighQualityOutlined />
              </div>
              <div className={styles.text}>
                {t("hq-stations")}
                <p>{t("premium.hq-stations-desc")}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showAuth && (
        <AuthGuardPopup
          onSuccess={authSuccessCallback}
          onClose={authCloseCallback}
        />
      )}
    </div>
  );
};
